import { create } from 'zustand';
import toast from 'react-hot-toast';
import { api } from '../lib/api';

interface CompanyUpload {
  aktiven: boolean;
  skupaj: number;
  koncano: number;
  podvojeni: number;
  napake: number;
  companyNaziv: string;
  tip: string;
  vrsta: File[];
  uvozeni: any[];
  podvojeniSeznam: any[];
  izpusceni: number;        // [PATCH uvoz-izpust-dnevnik v1]
  izpusceniSeznam: any[];
}

interface ZadnjiNalozen { companyId: string; doc: any; n: number; }

/* [PATCH sop-napredek-store v1] stanje SOPA (uvoz prek gumba "Uvoz").
   Loceno od poPodjetju: posamicni uvoz izvaja ta store sam v brskalniku,
   sop pa tece na strezniku (Celery) in ga tu samo spremljamo. */
interface SopUpload {
  batchId: string;
  companyNaziv: string;
  docType: string;
  aktiven: boolean;
  zakljucenOb: number;   // Date.now() ob prehodu v koncno stanje, sicer 0
  podatki: any;          // cel odgovor GET /dokumenti/{cid}/batch/{bid}
}

interface UploadState {
  poPodjetju: Record<string, CompanyUpload>;
  sopPoPodjetju: Record<string, SopUpload>;   // [PATCH sop-napredek-store v1]
  zadnjiNalozen: ZadnjiNalozen | null;
  verzija: number;
  zadnjiCompanyId: string | null;
  zazeniUvoz: (companyId: string, files: File[], tip: string, companyNaziv: string) => void;
  zacniSop: (companyId: string, batchId: string, companyNaziv: string, docType: string) => void;   // [PATCH sop-napredek-store v1]
  obnoviSop: (companyId: string, companyNaziv: string) => void;                                    // [PATCH sop-napredek-store v1]
  pozabiSop: (companyId: string) => void;                                                          // [PATCH sop-napredek-store v1]
}

export const useUploadStore = create<UploadState>((set, get) => {
  const upd = (cid: string, patch: Partial<CompanyUpload>) => {
    const m = get().poPodjetju;
    const cur = m[cid];
    if (!cur) return;
    set({ poPodjetju: { ...m, [cid]: { ...cur, ...patch } } });
  };

  const obdelaj = async (cid: string) => {
    while (true) {
      const cur = get().poPodjetju[cid];
      if (!cur || cur.vrsta.length === 0) break;
      const file = cur.vrsta[0];
      /* [PATCH eslog-priloge-fe v1] e-racun = datoteke z ISTO osnovo imena
         (racun.xml + racun.pdf) -> EN dokument, EN POST. XML je glavna (nosi
         podatke), ostale gredo kot priloge; backend iz njih vzame PDF za
         predogled namesto nase vizualizacije. Brez tega bi PDF nastal kot
         locen (podvojen) dokument skozi OCR. */
      const _osnova = (n: string) => (n || '').replace(/\.[^.]+$/, '').trim().toLowerCase();
      const _o = _osnova(file.name);
      const _kand = cur.vrsta.filter(f => _osnova(f.name) === _o);
      /* [PATCH eslog-priloge-fe v1a] grupiramo SAMO e-racune: skupina mora
         vsebovati .xml. Dva locena skena z isto osnovo imena (racun.pdf +
         racun.jpg) tako ostaneta DVA dokumenta -- kot pred v1. */
      const _jeERacun = _kand.length > 1 && _kand.some(f => /\.xml$/i.test(f.name));
      const _skupina = _jeERacun ? _kand : [file];
      const _ostanek = _jeERacun
        ? cur.vrsta.filter(f => _osnova(f.name) !== _o)
        : cur.vrsta.slice(1);
      const _glavna = _skupina.find(f => /\.xml$/i.test(f.name)) || _skupina[0];
      const _priloge = _skupina.filter(f => f !== _glavna);
      upd(cid, { vrsta: _ostanek, skupaj: cur.skupaj - _priloge.length });
      const fd = new FormData();
      fd.append('file', _glavna);
      _priloge.forEach(p => fd.append('priloge', p));
      fd.append('tip_dokumenta', cur.tip);
      try {
        const resp = await api.post(`/dokumenti/${cid}/uvozi`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const c = get().poPodjetju[cid];
        if (c) upd(cid, { koncano: c.koncano + 1, uvozeni: [...(c.uvozeni || []), { interno_st: resp.data?.interno_st, dobavitelj: resp.data?.dobavitelj, st_racuna: resp.data?.doc_number, znesek: resp.data?.znesek }] });
        // dodaj nastali dokument neposredno (brez GET osvezitve)
        if (resp && resp.data) set({ zadnjiNalozen: { companyId: cid, doc: resp.data, n: (get().zadnjiNalozen?.n || 0) + 1 } });
        /* [PATCH tuja-firma-opozorilo v1] uvozen, a ni naslovljen na to bazo */
        const _tfU: any = (resp?.data as any)?.kontiranje?.tuja_firma;
        if (_tfU && _tfU.aktivno) { toast(String(_tfU.sporocilo || ''), { icon: '\u26a0\ufe0f', duration: 10000 }); }
      } catch (e: any) {
        const jeP = e?.response?.status === 409;
        const d: any = jeP ? (e?.response?.data?.detail || {}) : null;
        /* [PATCH uvoz-izpust-dnevnik v1] 400 + detail.izpuscen = predracun/
           ponudba (kontrola uvoz-izpust-predracun) — steje se loceno, ne
           kot napaka; toast pokaze cloveski razlog (detail je objekt). */
        const _dIzp: any = e?.response?.data?.detail;
        const jeIzp = !jeP && e?.response?.status === 400 && !!(_dIzp && typeof _dIzp === 'object' && _dIzp.izpuscen);
        const c = get().poPodjetju[cid];
        if (c) upd(cid, { koncano: c.koncano + 1, podvojeni: c.podvojeni + (jeP ? 1 : 0), izpusceni: (c.izpusceni || 0) + (jeIzp ? 1 : 0), napake: c.napake + (jeP || jeIzp ? 0 : 1), podvojeniSeznam: jeP ? [...(c.podvojeniSeznam || []), { dobavitelj: d?.dobavitelj, st_racuna: d?.st_racuna, znesek: d?.znesek, obstojeca_interno_st: d?.obstojeca_interno_st }] : c.podvojeniSeznam, izpusceniSeznam: jeIzp ? [...(c.izpusceniSeznam || []), { st_racuna: _dIzp?.st_racuna, razlog: _dIzp?.sporocilo || _dIzp?.tip }] : c.izpusceniSeznam });
        if (jeIzp && _dIzp?.sporocilo) { toast.error(String(_dIzp.sporocilo), { duration: 8000 }); }
        /* [PATCH tuja-firma-toast v1] pokazi razlog zavrnitve (npr. tuja firma) na ekran */
        const _det = e?.response?.data?.detail;
        if (!jeP && typeof _det === 'string' && _det) {
          toast.error(_det, { duration: 8000 });
          /* [PATCH tuja-firma-dnevnik v1] zabelezi napacno bazo v dnevnik */
          if (_det.indexOf('NI naslovljen') >= 0) {
            api.post(`/dnevnik/${cid}`, { vrsta: 'uvoz', opis: 'Zavrnjeno — napačna baza: ' + _det }).catch(() => {});
          }
        }
      }
    }
    const f = get().poPodjetju[cid];
    if (f) {
      const _izp = f.izpusceni || 0;  /* [PATCH uvoz-izpust-dnevnik v1] */
      const uspesno = f.koncano - f.podvojeni - f.napake - _izp;
      let p = `Uvoz končan${f.companyNaziv ? ' (' + f.companyNaziv + ')' : ''}: ${uspesno} uspešno`;
      if (f.podvojeni) p += `, ${f.podvojeni} podvojenih`;
      if (_izp) p += `, ${_izp} izpuščenih (predračun/ponudba)`;
      if (f.napake) p += `, ${f.napake} z napako`;
      toast.success(p, { duration: 6000 });
      api.post(`/dnevnik/${cid}`, { vrsta: 'uvoz', opis: p, meta: { skupaj: f.skupaj, uspesno: uspesno, podvojeni: f.podvojeni, napake: f.napake, izpusceni_st: _izp, podjetje: f.companyNaziv, uvozeni: f.uvozeni, podvojeni_seznam: f.podvojeniSeznam, izpusceni: f.izpusceniSeznam || [] } }).catch(()=>{});
      upd(cid, { aktiven: false });
    }
    set({ zadnjiCompanyId: cid, verzija: get().verzija + 1 });
  };

  /* ===== [PATCH sop-napredek-store v1] napredek SOPA =====
     Sop obdeluje Celery worker; frontend samo polla stanje batcha.
     Polling je namenoma TU (ne v komponenti), da tece naprej tudi ko je
     okno zaprto ali je stran odmontirana. Kljuc je company_id — enako
     kot pri poPodjetju. */
  const SOP_KONCNI = ['koncano', 'napaka', 'razveljavljen'];
  const _sopTece: Record<string, boolean> = {};   // batchId -> polling ze tece
  const _sopNapak: Record<string, number> = {};   // batchId -> zaporedne mrezne napake

  const _sopUpd = (cid: string, patch: any) => {
    const m = get().sopPoPodjetju;
    const cur = m[cid];
    if (!cur) return;                              // pozabiSop je vmes pocistil
    set({ sopPoPodjetju: { ...m, [cid]: { ...cur, ...patch } } });
  };

  const _sopTik = async (cid: string, batchId: string) => {
    if (!_sopTece[batchId]) return;
    try {
      const r = await api.get(`/dokumenti/${cid}/batch/${batchId}`);
      const d = r.data || {};
      _sopNapak[batchId] = 0;
      const konec = SOP_KONCNI.indexOf(String(d.status || '')) >= 0;
      _sopUpd(cid, konec
        ? { podatki: d, aktiven: false, zakljucenOb: Date.now() }
        : { podatki: d, aktiven: true });
      if (konec) {
        delete _sopTece[batchId];
        delete _sopNapak[batchId];
        set({ zadnjiCompanyId: cid, verzija: get().verzija + 1 });   // zvonec se osvezi takoj
        return;
      }
    } catch {
      /* mrezna napaka: poskusi znova, a ne v nedogled */
      _sopNapak[batchId] = (_sopNapak[batchId] || 0) + 1;
      if (_sopNapak[batchId] > 40) {               // ~100 s brez odgovora
        delete _sopTece[batchId];
        delete _sopNapak[batchId];
        _sopUpd(cid, { aktiven: false });
        return;
      }
    }
    if (_sopTece[batchId]) setTimeout(() => { _sopTik(cid, batchId); }, 2500);
  };

  return {
    poPodjetju: {},
    sopPoPodjetju: {},   // [PATCH sop-napredek-store v1]
    zadnjiNalozen: null,
    verzija: 0,
    zadnjiCompanyId: null,
    /* [PATCH sop-napredek-store v1] zacni slediti sopu — klice DokumentiPage
       takoj po POST /batch oz. /batch-multi. Placeholder 'podatki' se vpise
       TAKOJ, da modal ne utripne nazaj na izbirnik datotek. */
    zacniSop: (companyId, batchId, companyNaziv, docType) => {
      if (!companyId || !batchId) return;
      const m = get().sopPoPodjetju;
      set({ sopPoPodjetju: { ...m, [companyId]: {
        batchId,
        companyNaziv: companyNaziv || '',
        docType: docType || '',
        aktiven: true,
        zakljucenOb: 0,
        podatki: { batch_id: batchId, status: 'v_vrsti', st_dokumentov: 0, st_koncano: 0, st_preskoceno: 0, st_napak: 0 },
      } } });
      if (_sopTece[batchId]) return;               // ze sledimo
      _sopTece[batchId] = true;
      _sopNapak[batchId] = 0;
      setTimeout(() => { _sopTik(companyId, batchId); }, 800);
    },
    /* [PATCH sop-napredek-store v1] obnova po osvezitvi strani (F5): ce za
       podjetje obstaja batch, ki ni v koncnem stanju, prevzemi polling.
       Bere obstojeci GET /dokumenti/{cid}/batch — brez novega endpointa. */
    obnoviSop: async (companyId, companyNaziv) => {
      if (!companyId) return;
      const cur = get().sopPoPodjetju[companyId];
      if (cur && cur.aktiven) return;              // ze sledimo
      try {
        const r = await api.get(`/dokumenti/${companyId}/batch`);
        const seznam: any[] = Array.isArray(r.data) ? r.data : [];
        const b = seznam.find((x: any) => SOP_KONCNI.indexOf(String((x && x.status) || '')) < 0);
        if (!b || !b.batch_id) return;
        get().zacniSop(companyId, b.batch_id, companyNaziv || '', b.doc_type || '');
      } catch { /* brez obnove — ni kriticno */ }
    },
    /* [PATCH sop-napredek-store v1] pocisti stanje sopa (nov uvoz, razveljavitev) */
    pozabiSop: (companyId) => {
      if (!companyId) return;
      const m = get().sopPoPodjetju;
      const cur = m[companyId];
      if (cur && cur.batchId) { delete _sopTece[cur.batchId]; delete _sopNapak[cur.batchId]; }
      const n: any = { ...m };
      delete n[companyId];
      set({ sopPoPodjetju: n });
    },
    zazeniUvoz: (companyId, files, tip, companyNaziv) => {
      if (!files || files.length === 0) return;
      const m = get().poPodjetju;
      const cur = m[companyId];
      if (cur && cur.aktiven) {
        set({ poPodjetju: { ...m, [companyId]: { ...cur, vrsta: [...cur.vrsta, ...files], skupaj: cur.skupaj + files.length, companyNaziv, tip } } });
        return;
      }
      set({ poPodjetju: { ...m, [companyId]: { aktiven: true, skupaj: files.length, koncano: 0, podvojeni: 0, napake: 0, izpusceni: 0, uvozeni: [], podvojeniSeznam: [], izpusceniSeznam: [], companyNaziv, tip, vrsta: [...files] } } });  /* [PATCH uvoz-izpust-dnevnik v1] */
      obdelaj(companyId);
    },
  };
});
